import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PencilIcon, PlusIcon } from "@/components/developer/DashboardIcons";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import CertificationForm from "./certifications/CertificationForm";
import CertificationList from "./certifications/CertificationList";

const Certifications = () => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editingCertification, setEditingCertification] = useState<any>(null);

  const { data: certifications, isFetching, refetch } = useQuery({
    queryKey: ['certifications'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('certifications')
        .select('*')
        .eq('user_id', user.user.id)
        .order('issued_date', { ascending: false });

      if (error) {
        toast({
          title: "Error",
          description: "Failed to fetch certifications data",
          variant: "destructive",
        });
        throw error;
      }

      return data;
    }
  });

  const handleSave = async (certificationData: any) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      if (editingCertification) {
        const { error } = await supabase
          .from('certifications')
          .update({
            name: certificationData.name,
            issuer: certificationData.issuer,
            issued_date: certificationData.issued_date,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingCertification.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Certification updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('certifications')
          .insert({
            user_id: user.user.id,
            name: certificationData.name,
            issuer: certificationData.issuer,
            issued_date: certificationData.issued_date,
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Certification added successfully",
        });
      }

      await refetch();
      setIsEditing(false);
      setEditingCertification(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save certification",
        variant: "destructive",
      });
      console.error('Error saving certification:', error);
    }
  };

  const handleEdit = (certification: any) => {
    setEditingCertification(certification);
    setIsEditing(true);
  };

  if (isFetching) {
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Certifications</h2>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-16 w-full bg-gray-200 rounded"></div>
          <div className="h-16 w-full bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  if (isEditing) {
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Certifications</h2>
        </div>
        <CertificationForm
          initialData={editingCertification}
          onSave={handleSave}
          onCancel={() => {
            setIsEditing(false);
            setEditingCertification(null);
          }}
        />
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <h2 className="text-xl font-semibold">Certifications</h2>
        <div className="flex gap-2">
          {certifications && certifications.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setEditingCertification(null);
                setIsEditing(true);
              }}
              className="gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Add
            </Button>
          )}
        </div>
      </div>
      {(!certifications || certifications.length === 0) ? (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 mb-4">No certifications yet</p>
          <Button
            onClick={() => {
              setEditingCertification(null);
              setIsEditing(true);
            }}
            className="gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Certification
          </Button>
        </div>
      ) : (
        <CertificationList 
          certifications={certifications} 
          onEdit={handleEdit}
        />
      )}
    </Card>
  );
};

export default Certifications;